import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import Notification from '../../components/notification/notification';
import userApi from '../../services/userApi';
import User from '../../assets/user.svg';
import { PageHeader, TWButton, TWInput, TWModal } from '../../components/ui';
import './profile.css';

const Profile = () => {
  const user = useSelector((state) => state.authReducer.user);
  const [form, setForm] = useState({
    fullname: user?.fullname,
    email: user?.email,
    password: '',
    confirmpassword: '',
  });
  const [loading, setLoading] = useState({ upsertLoading: false });
  const [errors, setErrors] = useState({
    fullname: false,
    email: false,
    password: false,
  });
  const [dialogs, setDialogs] = useState({ upsert: false });
  const cancelButtonRef = useRef(null);
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  const onFormSubmit = async () => {
    if (
      !form.fullname ||
      !form.email ||
      form.password !== form.confirmpassword
    ) {
      setErrors({
        fullname: !form.fullname,
        email: !form.email,
        password: form.password !== form.confirmpassword,
      });
      return;
    }
    setErrors({ fullname: false, email: false, password: false });
    setLoading((prev) => ({ ...prev, upsertLoading: true }));
    try {
      const res = await userApi.updateProfile(form);
      if (res.status === 200) {
        setNotf({
          display: true,
          type: 'success',
          message: 'Profile updated successfully',
        });
      }
    } catch (err) {
      setNotf({
        display: true,
        type: 'error',
        message:
          err.response.data.errors[Object.keys(err.response.data.errors)[0]][0],
      });
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onFormChange = (val, key) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const onClickEdit = () => {
    setForm({
      fullname: user?.fullname || '',
      email: user?.email || '',
      password: '',
      confirmpassword: '',
    });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onCloseUpsert = () => {
    setErrors({ fullname: false, email: false, password: false });
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  return (
    <div className="profile-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Profile"
          description="Update and manage your personal profile information."
        />

        <section className="reveal overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div className="flex flex-col gap-6 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-center sm:px-6 dark:border-slate-700">
            <img
              src={User}
              className="h-24 w-24 rounded-full object-cover sm:h-28 sm:w-28"
              alt="profile"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-lg font-bold text-slate-900 dark:text-slate-100">
                {user?.fullname}
              </p>
              <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                {user?.email}
              </p>
            </div>
            <TWButton
              variant="primary"
              size="lg"
              icon={PencilSquareIcon}
              label="Edit profile"
              onClick={onClickEdit}
            />
          </div>

          <div className="px-5 py-5 sm:px-6">
            <dl className="divide-y divide-slate-100 dark:divide-slate-700">
              <div className="grid grid-cols-1 gap-2 py-4 sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  Full name
                </dt>
                <dd className="text-sm text-slate-900 sm:col-span-2 dark:text-slate-100">
                  {user?.fullname || '-'}
                </dd>
              </div>
              <div className="grid grid-cols-1 gap-2 py-4 sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  Email
                </dt>
                <dd className="text-sm text-slate-900 sm:col-span-2 dark:text-slate-100">
                  {user?.email || '-'}
                </dd>
              </div>
              <div className="grid grid-cols-1 gap-2 py-4 sm:grid-cols-3 sm:gap-4">
                <dt className="text-sm font-medium text-slate-500 dark:text-slate-400">
                  Password
                </dt>
                <dd className="text-sm text-slate-900 sm:col-span-2 dark:text-slate-100">
                  ******
                </dd>
              </div>
            </dl>
          </div>
        </section>
      </div>

      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title="Edit profile"
        size="xl"
        initialFocus={cancelButtonRef}
        showCloseButton
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <TWButton
              ref={cancelButtonRef}
              label="Cancel"
              disabled={loading.upsertLoading}
              onClick={onCloseUpsert}
            />
            <TWButton
              variant="primary"
              label="Update profile"
              loading={loading.upsertLoading}
              disabled={loading.upsertLoading}
              onClick={onFormSubmit}
            />
          </div>
        }
      >
        <div className="space-y-5">
          <TWInput
            label="Full Name"
            value={form.fullname}
            onChange={(e) => onFormChange(e.target.value, 'fullname')}
            placeholder="e.g. 'John Doe'"
            error={errors.fullname ? 'Full name can not be empty' : undefined}
          />
          <TWInput
            label="Email"
            value={form.email}
            onChange={(e) => onFormChange(e.target.value, 'email')}
            placeholder="e.g. 'example@acme.com'"
            error={errors.email ? 'Email can not be empty' : undefined}
          />

          <div>
            <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Change Password
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Leave both fields empty if you do not want to change it.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TWInput
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) => onFormChange(e.target.value, 'password')}
            />
            <TWInput
              label="Confirm Password"
              type="password"
              value={form.confirmpassword}
              onChange={(e) => onFormChange(e.target.value, 'confirmpassword')}
              error={errors.password ? 'Passwords does not match' : undefined}
            />
          </div>
        </div>
      </TWModal>

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default Profile;
